import _noop from 'lodash-es/noop';
import _get from 'lodash-es/get';
import React, { useMemo, useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  CHECKOUT_DETAILS,
  REDIRECT_URL_LOGEED_OUT_ONLINE_PAYMENT,
  REDIRECT_URL_ONLINE_PAYMENT,
} from '../../../../constants/urls';

import zfetch from '../../../common/utils/zfetch';
import ZpayKitLoader from '../ZpayKitLoader';
import { genericErrorMsg } from '../../../../constants';
import { PAYMENTS_ROUTE } from '../../../../constants/routes';
import {
  applicableFlowTypes,
  currentFlowTypeEnum,
  paymentPurposeEnum,
  programTypeEnum,
} from '../../../common/components/constants';

const PaymentKit = ({
  currentFlow,
  accessCode,
  grandTotal,
  userEmail,
  userPhone,
  additionalPaymentParams,
  countryId,
  serviceType,
  companyId,
  onlinePaymentsFlag,
  isMobile,
  paymentStateValidationStateChangeHandler,
  isCheckoutStarted,
  isCheckoutEnded,
  paymentStateMarkOrderCreated,
  paymentStateCheckoutFailed,
  showErrorToast,
  initiateCheckout,
  selectedWorkspace,
  setIsCheckoutStarted,
  setLoadingCheckout,
  invoiceDetails,
  adhocState,
  loggedOutInfo,
}) => {
  const [makePaymentParams, setMakePaymentsParams] = useState({});
  const [isPlaceOrderSuccess, setIsPlaceOrderSuccess] = useState(false);
  const [redirectPage, setRedirectPage] = useState(false);
  const zpaykitArgs = useMemo(
    () => ({
      phone: userPhone,
      email: userEmail,
      amount: grandTotal,
      // additional_params: JSON.s,
    }),
    [userPhone, userEmail, grandTotal, additionalPaymentParams],
  );

  const kitInitParams = useMemo(
    () => ({
      country_id: countryId,
      code: accessCode,
      phone: userPhone,
      email: userEmail,
      amount: grandTotal,
      order_type: serviceType,
      additional_params: JSON.stringify({
        hidden_payment_methods: [
          {
            payment_method_type: 'simpl',
          },
        ],
      }),
      online_payments_flag: 1,
      locale: 'en',
      service_type: serviceType,
      host_redirect_url: adhocState
        ? `${REDIRECT_URL_LOGEED_OUT_ONLINE_PAYMENT}/${loggedOutInfo.payment_request_id ??
        ''}`
        : REDIRECT_URL_ONLINE_PAYMENT,
      isNaked: false, // True in case of webfrontend kits
      isMobileView: isMobile,
      manageErrorInternally: !isMobile,
      companyId,
    }),
    [
      isMobile,
      countryId,
      accessCode,
      userPhone,
      userEmail,
      grandTotal,
      additionalPaymentParams,
      onlinePaymentsFlag,
      companyId,
    ],
  );

  const onValidationStatusChange = (status, paymentParams) => {
    console.log(paymentParams);
    paymentStateValidationStateChangeHandler({
      status,
    });
  };

  const placeOrder = async function (sdkPaymentInfo, callback) {
    const {
      payment_method_id: paymentMethodId,
      payment_method_type: paymentMethodType,
    } = sdkPaymentInfo;
    const amountBalance = Number.parseFloat(
      localStorage.onlinePaymentAmount ?? 0,
    );
    let reqBody = JSON.stringify({
      workspace_id: selectedWorkspace?.workspace_id ?? '',
      payment_method_type: paymentMethodType,
      payment_method_id: paymentMethodId,
      amount: amountBalance,
      service_type: serviceType,
      country_id: countryId,
      company_id: companyId,
      program_type: programTypeEnum.PROGRAM_TYPE_RECURRING,
      payment_purpose: paymentPurposeEnum.PAYMENT_PURPOSE_PPI,
    });
    if (adhocState) {
      reqBody = JSON.stringify({
        payment_method_type: paymentMethodType,
        payment_method_id: paymentMethodId,
        amount: loggedOutInfo.balance ?? 0,
        payment_request_id: loggedOutInfo.payment_request_id ?? '',
        service_type: serviceType,
        country_id: countryId,
        company_id: companyId,
        program_type: programTypeEnum.PROGRAM_TYPE_ADHOC,
        payment_purpose: paymentPurposeEnum.PAYMENT_PURPOSE_ADHOC,
      });
    } else if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
      reqBody = JSON.stringify({
        workspace_id: selectedWorkspace?.workspace_id ?? '',
        payment_method_type: paymentMethodType,
        payment_method_id: paymentMethodId,
        amount: amountBalance,
        service_type: serviceType,
        country_id: countryId,
        company_id: companyId,
        invoice_id: invoiceDetails.invoice_id,
        start_date: invoiceDetails.start_date,
        end_date: invoiceDetails.end_date,
        program_type: programTypeEnum.PROGRAM_TYPE_RECURRING,
        payment_purpose: paymentPurposeEnum.PAYMENT_PURPOSE_INVOICE,
      });
    }
    zfetch(CHECKOUT_DETAILS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: reqBody,
    })
      .then(res => res.json())
      .then(res => {
        const data = _get(res, 'data');
        setMakePaymentsParams({
          order_id: _get(data, 'order_id'),
          payments_hash: _get(data, 'payment_hash'),
        });
        callback({
          order_id: _get(data, 'order_id'),
          payments_hash: _get(data, 'payment_hash'),
        });
        setIsPlaceOrderSuccess(true);
      })
      .catch(error => {
        console.error(error);
        showErrorToast(genericErrorMsg);
        setRedirectPage(true);
      });
  };

  const onPlaceOrder = useCallback(
    async (success, kitError = '', paymentInfo) => {
      const sdkPaymentInfo = success ? paymentInfo : {};
      placeOrder(sdkPaymentInfo, function ({ order_id, payments_hash }) {
        paymentStateMarkOrderCreated({
          orderId: order_id,
          paymentHash: payments_hash,
        });
      });
      if (!success) {
        console.error(kitError);
        showErrorToast(kitError);
      }
    },
    [placeOrder],
  );

  const internalCheckoutClickHandler = useCallback(() => {
    initiateCheckout();
  }, []);

  const paymentErrorHandler = errorState => {
    setIsCheckoutStarted(false);
    setLoadingCheckout(false);
    paymentStateCheckoutFailed(errorState);
    setIsPlaceOrderSuccess(false);
  };

  const onZpayKitError = (errorType, errorMessage) => {
    const errorState = {
      code: 0,
      errorMessage,
      errorData: errorType,
    };
    paymentErrorHandler(errorState);
  };

  return redirectPage ? (
    <Navigate to={PAYMENTS_ROUTE} replace />
  ) : (
    <ZpayKitLoader
      onWidgetLoad={_noop}
      onValidationStatusChange={onValidationStatusChange}
      onPlaceOrder={onPlaceOrder}
      onInternalCheckoutClick={internalCheckoutClickHandler}
      onZpayKitError={onZpayKitError}
      onUpiPaymentProgress={_noop}
      isCheckoutInititated={isCheckoutStarted}
      isCheckoutEnded={isCheckoutEnded}
      isPlaceOrderSuccessful={isPlaceOrderSuccess}
      makePaymentParams={makePaymentParams}
      kitInitParams={kitInitParams}
      zpaykitArgs={zpaykitArgs}
      accessCode={accessCode}
    />
  );
};

PaymentKit.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  grandTotal: PropTypes.string.isRequired,
  userEmail: PropTypes.string.isRequired,
  userPhone: PropTypes.string.isRequired,
  // onCheckoutClick: PropTypes.func.isRequired,
  accessCode: PropTypes.string.isRequired,
  // setCheckoutState: PropTypes.func.isRequired,
  additionalPaymentParams: PropTypes.string,
  onlinePaymentsFlag: PropTypes.number,
  // closeModalHandler: PropTypes.func,
  isMobile: PropTypes.bool,
  isCheckoutEnded: PropTypes.bool,
  isCheckoutStarted: PropTypes.bool,
  paymentStateValidationStateChangeHandler: PropTypes.func,
  paymentStateMarkOrderCreated: PropTypes.func,
  paymentStateCheckoutFailed: PropTypes.func,
  showErrorToast: PropTypes.func,
  initiateCheckout: PropTypes.func,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  serviceType: PropTypes.number,
  companyId: PropTypes.number,
  countryId: PropTypes.number,
  setIsCheckoutStarted: PropTypes.func,
  setLoadingCheckout: PropTypes.func,
  invoiceDetails: PropTypes.objectOf(PropTypes.any),
  adhocState: PropTypes.bool,
  loggedOutInfo: PropTypes.objectOf(PropTypes.any),
};

PaymentKit.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  additionalPaymentParams: '',
  // closeModalHandler: _noop,
  onlinePaymentsFlag: 1,
  isMobile: 0,
  isCheckoutEnded: false,
  isCheckoutStarted: false,
  paymentStateValidationStateChangeHandler: _noop,
  paymentStateMarkOrderCreated: _noop,
  paymentStateCheckoutFailed: _noop,
  showErrorToast: _noop,
  initiateCheckout: _noop,
  selectedWorkspace: {},
  countryId: 1,
  serviceType: 1,
  companyId: 1,
  setIsCheckoutStarted: _noop,
  setLoadingCheckout: _noop,
  invoiceDetails: {},
  adhocState: false,
  loggedOutInfo: {},
};

export default PaymentKit;
